/**
 * 案件の欄（039、docs/28）。新しい案件・案件を直す・新しい見積書・見積の紙の上で同じ部品を使う。
 *   ・BranchSelect   … 支店・営業所（顧客に支店があるときだけ出す）
 *   ・ContactSelect  … 顧客担当者（指定しない／この顧客の担当者／「新しい担当者を登録…」＝その場で登録）
 *   ・TermsChooser   … 支払条件（顧客に従う／この案件だけ変える＝締め日・支払月・支払日）
 *   ・defaultsOf     … 顧客を選んだときの既定＝主担当と、その人が付いている支店
 * 見た目は試作 screen_sample/現場力_案件_案_v1.html
 * 9/29（#92、docs/35）：欄の添え書き・下の説明の文は無くした。説明は使う画面の「？」に partyFieldsHelp / termsHelp を足す
 * 9/29（#96、docs/36）：作成画面（新しい見積書・新しい案件）は float＝浮く名前の欄、TermsChooser は variant="cards"＝押せるカード2枚
 */
import {
  DEFAULT_PAYMENT_TERMS,
  ESTIMATE_PC_MSG,
  TERM_DAY_LABEL,
  TERM_MONTH_LABEL,
  paymentTermsLabel,
  type Partner,
  type PartnerContactInput,
  type Labels,
  type PaymentTerms,
  type TermDay,
  type TermMonth,
} from "@genbaryoku/contract";
import { useMutation } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { api, ApiError, messageOf } from "../api.js";
import { useLabels } from "../labels.js";
import { Choice } from "./Create.jsx";
import { ErrorText, guardComposingEnter } from "./ui.jsx";

/** 支店・{customer}担当者の欄の説明（画面の「？」に足す。#92） */
export function partyFieldsHelp(L: Labels): string {
  return `支店・営業所は宛先です（無ければ本社）。${L.customer}担当者を選ぶと、帳票の宛名に「部署 氏名 様」と印字します。その場で登録した担当者の氏名は暗号化して保存し、${L.customer}の画面の「担当者」にも入ります。`;
}

/** 支払条件の欄の説明（画面の「？」に足す。#92） */
export function termsHelp(L: Labels): string {
  return `支払条件は請求書のお支払期限に使います。消費税の丸め・どちらを適格請求書にするかは、${L.customer}の設定のままです。`;
}

/** 顧客を選んだときの既定：主担当（いれば）と、その人が支店付きならその支店 */
export function defaultsOf(p: Partner | undefined | null): { branchId: string | null; contactId: string | null } {
  const k = p?.contacts.find((c) => c.isPrimary) ?? null;
  return { branchId: k?.branchId ?? null, contactId: k?.id ?? null };
}

export function BranchSelect({ partner, value, onChange, id, disabled, float, onFocus, onBlur }: { partner: Partner; value: string | null; onChange: (v: string | null) => void; id: string; disabled?: boolean; /** 浮く名前の欄（作成画面、#96） */ float?: boolean; onFocus?: () => void; onBlur?: () => void }) {
  if (!partner.branches.length) return null;
  const select = (
    <select id={id} className="inp" value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value || null)} onFocus={onFocus} onBlur={onBlur}>
      <option value="">本社（支店なし）</option>
      {partner.branches.map((b) => (
        <option key={b.id} value={b.id}>{b.name}</option>
      ))}
    </select>
  );
  if (float) {
    return (
      <div className="field">
        {select}
        <label htmlFor={id}>支店・営業所</label>
      </div>
    );
  }
  return (
    <div className="fld">
      <label className="lbl" htmlFor={id}>支店・営業所</label>
      {select}
    </div>
  );
}

/**
 * 顧客担当者。選ぶと紙の宛名が「会社名／支店名／部署 氏名 様」になる（御中は付けない）。
 * 「新しい担当者を登録…」は 氏名・部署 の2欄で、その場で顧客の担当者に足す（マスタを先に作らせない）
 */
export function ContactSelect({
  partner,
  branchId,
  value,
  onChange,
  onPartner,
  id,
  disabled,
  compact,
  row,
  help,
  float,
  onFocus,
  onBlur,
}: {
  partner: Partner;
  branchId: string | null;
  value: string | null;
  onChange: (v: string | null) => void;
  /** 担当者を登録したあとの顧客（呼んだ側が持っている顧客を差し替える） */
  onPartner: (p: Partner) => void;
  id: string;
  disabled?: boolean;
  /** 見積の紙の上（ラベルを横に置く小さい形） */
  compact?: boolean;
  /** 見積書の「詳細」の1行（#187：名前｜選ぶ欄｜右に「＋ 新しい担当者を登録」。登録の欄は行の下に開く） */
  row?: boolean;
  /** row のとき選ぶ欄の右に置く「？」（#187：前は見積書の題の「？」の中） */
  help?: ReactNode;
  /** 浮く名前の欄（作成画面、#96） */
  float?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  const L = useLabels();
  const [adding, setAdding] = useState<{ name: string; department: string } | null>(null);
  const [error, setError] = useState<string | undefined>();
  const list = [...partner.contacts].sort((a, b) => Number(b.branchId === branchId && !!branchId) - Number(a.branchId === branchId && !!branchId));
  const branchName = (bid: string | null) => (bid ? partner.branches.find((b) => b.id === bid)?.name ?? "" : "");
  const add = useMutation({
    mutationFn: (v: { name: string; department: string }) =>
      api<Partner>("POST", `/partners/${partner.id}/contacts`, { name: v.name, department: v.department, isPrimary: partner.contacts.length === 0, branchId } satisfies Partial<PartnerContactInput>),
    onSuccess: (np) => {
      const before = new Set(partner.contacts.map((c) => c.id));
      const created = np.contacts.find((c) => !before.has(c.id));
      onPartner(np);
      setAdding(null);
      setError(undefined);
      if (created) onChange(created.id);
    },
    onError: (e) => setError(e instanceof ApiError && e.problem.errors?.name ? e.problem.errors.name : messageOf(e)),
  });
  const select = (
    <select
      id={id}
      className={compact ? undefined : "inp"}
      value={adding ? "__new" : (value ?? "")}
      disabled={disabled}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={(e) => {
        if (e.target.value === "__new") {
          setAdding({ name: "", department: "" });
          setError(undefined);
          return;
        }
        setAdding(null);
        onChange(e.target.value || null);
      }}
    >
      <option value="">{row ? ESTIMATE_PC_MSG.contactNone : "指定しない"}</option>
      {list.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
          {c.department ? `（${c.department}）` : ""}
          {c.branchId ? `・${branchName(c.branchId)}` : ""}
        </option>
      ))}
      {/* row（見積書の詳細）は行の右の「＋ 新しい担当者を登録」で開く（Figma 172:175） */}
      {row ? null : <option value="__new">＋ 担当者新規登録…</option>}
    </select>
  );
  const form = adding ? (
    <div className="inlinenew" data-testid="contact-new">
      <div className="field">
        <label htmlFor={`${id}_n`}>氏名</label>
        <input
          id={`${id}_n`}
          className="inp"
          autoFocus
          value={adding.name}
          onChange={(e) => setAdding({ ...adding, name: e.target.value })}
          onKeyDown={(e) => {
            guardComposingEnter(e);
            if (e.key === "Enter" && !e.defaultPrevented) {
              e.preventDefault();
              if (adding.name.trim()) add.mutate({ name: adding.name.trim(), department: adding.department.trim() });
            }
          }}
        />
      </div>
      <div className="field">
        <label htmlFor={`${id}_d`}>部署<span className="opt">任意</span></label>
        <input id={`${id}_d`} className="inp" value={adding.department} onChange={(e) => setAdding({ ...adding, department: e.target.value })} />
      </div>
      <button type="button" className="btn" disabled={add.isPending || !adding.name.trim()} onClick={() => add.mutate({ name: adding.name.trim(), department: adding.department.trim() })}>
        登録して選択
      </button>
      <button type="button" className="btn quiet" onClick={() => { setAdding(null); setError(undefined); }}>取消</button>
      <div style={{ flexBasis: "100%" }}><ErrorText>{error}</ErrorText></div>
    </div>
  ) : null;
  if (row) {
    return (
      <>
        <div className="edrow w-280">
          <label className="edl" htmlFor={id}>{L.customer}{ESTIMATE_PC_MSG.contact}</label>
          <div className="edv">
            {select}
            {help}
          </div>
          <div className="eda">
            {!disabled && !adding ? (
              <button type="button" className="btn text sm" data-testid="contact-add" onClick={() => { setAdding({ name: "", department: "" }); setError(undefined); }}>
                ＋ {ESTIMATE_PC_MSG.contactAdd}
              </button>
            ) : null}
          </div>
        </div>
        {form ? <div className="edform">{form}</div> : null}
      </>
    );
  }
  if (compact) {
    return (
      <>
        <span className="brsel">
          <label htmlFor={id}>{L.customer}担当者</label>
          {select}
        </span>
        {form ? <div style={{ flexBasis: "100%" }}>{form}</div> : null}
      </>
    );
  }
  if (float) {
    return (
      <div className="crcontact">
        <div className="field">
          {select}
          <label htmlFor={id}>{L.customer}担当者</label>
        </div>
        {form}
      </div>
    );
  }
  return (
    <div className="fld">
      <label className="lbl" htmlFor={id}>{L.customer}担当者</label>
      {select}
      {form}
    </div>
  );
}

const DAYS: TermDay[] = [5, 10, 15, 20, 25, 99];
const MONTHS: TermMonth[] = [0, 1, 2];

/**
 * 支払条件。null＝顧客に従う。案件で変えられるのはお支払期限の決まりだけ（丸め・インボイスは顧客のまま）
 * variant="cards"（#96、新しい案件の段3）：「既定の条件」（下に今の条件の1行。#97 で「{customer}に合わせる」から）「この{project}だけ変える」の押せるカード2枚（role="radio"）。
 *   締め日などの欄は同じ。cards ではカードの下に 12px あけて淡い地の枠にまとめる（#97：カードに重なって見えた）。「＝ …」の行はどちらの形でも出さない（#98、射場さん「入力欄に大きく出ているので諄い」）
 */
export function TermsChooser({ clientTerms, value, onChange, idBase, variant = "seg", disabled }: { clientTerms: PaymentTerms | null; value: PaymentTerms | null; onChange: (v: PaymentTerms | null) => void; idBase: string; variant?: "seg" | "cards"; /** 押せない形（{customer}の情報を待つ間、#179。形と高さは同じ） */ disabled?: boolean }) {
  const L = useLabels();
  const base = clientTerms ?? DEFAULT_PAYMENT_TERMS;
  const sel = (id: string, label: string, v: number, opts: number[], fmt: (n: number) => string, set: (n: number) => void) => (
    <label className="row" style={{ gap: 6, alignItems: "center" }} htmlFor={id}>
      {label ? <span style={{ fontSize: "var(--fs-15)", color: "var(--ink2)" }}>{label}</span> : null}
      <select id={id} className="inp termsel" value={v} disabled={disabled} onChange={(e) => set(Number(e.target.value))}>
        {opts.map((o) => (
          <option key={o} value={o}>{fmt(o)}</option>
        ))}
      </select>
    </label>
  );
  return (
    <div className="fld">
      <span className={variant === "cards" ? "crsub" : "lbl"} id={`${idBase}_lbl`}>支払条件</span>
      {variant === "cards" ? (
        <div className="crchoices" role="radiogroup" aria-labelledby={`${idBase}_lbl`}>
          <Choice checked={!value} onClick={() => onChange(null)} title="既定の条件" sub={paymentTermsLabel(base)} testId={`${idBase}-client`} />
          <Choice checked={!!value} onClick={() => onChange(value ?? { ...base })} title={`この${L.project}だけ変更`} testId={`${idBase}-own`} />
        </div>
      ) : (
        <div className="seg fit" role="group" aria-labelledby={`${idBase}_lbl`}>
          <button type="button" aria-pressed={!value} disabled={disabled} onClick={() => onChange(null)}>{L.customer}に従う（{paymentTermsLabel(base)}）</button>
          <button type="button" aria-pressed={!!value} disabled={disabled} onClick={() => onChange(value ?? { ...base })}>この{L.project}だけ変更</button>
        </div>
      )}
      {value ? (
        <div className={variant === "cards" ? "crterms" : "termsbox"} data-testid={`${idBase}-box`}>
          <div className="termsrow">
            {sel(`${idBase}_c`, "締め日", value.closingDay, DAYS, (n) => TERM_DAY_LABEL(n as TermDay), (n) => onChange({ ...value, closingDay: n as TermDay }))}
            {sel(`${idBase}_m`, "支払", value.paymentMonthOffset, MONTHS, (n) => TERM_MONTH_LABEL[n as TermMonth], (n) => onChange({ ...value, paymentMonthOffset: n as TermMonth }))}
            {sel(`${idBase}_d`, "", value.paymentDay, DAYS, (n) => TERM_DAY_LABEL(n as TermDay), (n) => onChange({ ...value, paymentDay: n as TermDay }))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
